/**
 * Bank Account Dialog Component
 * 
 * Dialog for creating or editing bank accounts.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { bankAccountService } from "@/services/bankAccountService";
import { toast } from "sonner";
import type { BankAccount } from "@/services/bankAccountService";
import type { Bank } from "@/services/bankService";
import { LocationSelect } from "@/components/ui/LocationSelect";
import { locationService } from "@/services/locationService";

interface BankAccountDialogProps {
  open: boolean;
  onClose: (success?: boolean) => void;
  account?: BankAccount | null;
  banks: Bank[];
}

export function BankAccountDialog({ open, onClose, account, banks }: BankAccountDialogProps) {
  const [bankId, setBankId] = React.useState("");
  const [province, setProvince] = React.useState<string | undefined>(undefined);
  const [city, setCity] = React.useState<string | undefined>(undefined);
  const [accountNumber, setAccountNumber] = React.useState("");
  const [accountName, setAccountName] = React.useState("");
  const [tipe, setTipe] = React.useState<"ORDA" | "ORLOK" | "">("");
  const [isActive, setIsActive] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [bankSearchOpen, setBankSearchOpen] = React.useState(false);
  const [bankSearchQuery, setBankSearchQuery] = React.useState("");
  const [validating, setValidating] = React.useState(false);
  const [validationResponse, setValidationResponse] = React.useState<any>(null);
  const [provinces, setProvinces] = React.useState<string[]>([]);
  const [loadingProvinces, setLoadingProvinces] = React.useState(false);

  const isEdit = !!account;

  // Fetch provinces on mount
  React.useEffect(() => {
    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const data = await locationService.fetchProvinces();
        setProvinces(data);
      } catch (err) {
        console.error("Error fetching provinces:", err);
      } finally {
        setLoadingProvinces(false);
      }
    };

    fetchProvinces();
  }, []);

  // Filter banks based on search query
  const filteredBanks = React.useMemo(() => {
    const activeBanks = banks.filter((bank) => bank.isActive);
    if (!bankSearchQuery.trim()) {
      return activeBanks;
    }
    const query = bankSearchQuery.toLowerCase().trim();
    return activeBanks.filter(
      (bank) =>
        bank.name.toLowerCase().includes(query) ||
        bank.code.toLowerCase().includes(query)
    );
  }, [banks, bankSearchQuery]);

  // Get selected bank name
  const selectedBank = React.useMemo(() => {
    return banks.find((bank) => bank._id === bankId);
  }, [banks, bankId]);

  // Reset form when dialog opens/closes or account changes
  React.useEffect(() => {
    if (open) {
      if (account) {
        const bankIdValue = typeof account.bank === "string" ? account.bank : account.bank._id;
        setBankId(bankIdValue);
        setProvince(account.province || undefined);
        setCity(account.city || undefined);
        setAccountNumber(account.accountNumber);
        setAccountName(account.accountName);
        setTipe(account.tipe || "");
        setIsActive(account.isActive);
      } else {
        setBankId("");
        setProvince(undefined);
        setCity(undefined);
        setAccountNumber("");
        setAccountName("");
        setTipe("");
        setIsActive(true);
      }
      setError(null);
      setBankSearchQuery("");
      setBankSearchOpen(false);
      setValidationResponse(null);
    }
  }, [open, account]);

  const handleValidate = async () => {
    if (!accountNumber.trim()) {
      setError("Nomor rekening harus diisi");
      return;
    }

    if (!bankId) {
      setError("Bank harus dipilih terlebih dahulu");
      return;
    }

    setValidating(true);
    setError(null);
    setValidationResponse(null);

    try {
      const response = await bankAccountService.validateBankAccount({
        accountNumber: accountNumber.trim(),
        bankId: bankId,
      });

      setValidationResponse(response);
      
      // Check for error in response
      if (response.error === "invalid_parameters_in_request") {
        const errorMessage = "nomer rekening tidak valid";
        setError(errorMessage);
        toast.error(errorMessage);
        return;
      }
      
      // Check if validation was successful (status === 200)
      if (response.status === 200 && response.data?.accountName) {
        // Auto-populate account name field
        setAccountName(response.data.accountName);
        toast.success("Validasi rekening berhasil", {
          description: `Nama rekening: ${response.data.accountName}`,
        });
      } else if (response.status === 200) {
        toast.success("Validasi rekening berhasil", {
          description: "Data rekening telah divalidasi",
        });
      } else {
        // Non-200 status
        const errorMessage = response.error || "Gagal memvalidasi rekening";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal memvalidasi rekening";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!bankId) {
        setError("Bank harus dipilih");
        setLoading(false);
        return;
      }

      if (!accountNumber.trim()) {
        setError("Nomor rekening harus diisi");
        setLoading(false);
        return;
      }

      if (!accountName.trim()) {
        setError("Nama rekening harus diisi");
        setLoading(false);
        return;
      }

      if (!tipe) {
        setError("Tipe harus dipilih");
        setLoading(false);
        return;
      }

      if (!province) {
        setError("Provinsi harus dipilih");
        setLoading(false);
        return;
      }

      if (tipe === "ORLOK" && !city) {
        setError("Kota/Kabupaten harus dipilih");
        setLoading(false);
        return;
      }

      const accountData = {
        bank: bankId,
        province: province.trim(),
        city: tipe === "ORLOK" && city ? city.trim() : undefined,
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim(),
        tipe: tipe || undefined,
        isActive,
      };

      if (isEdit && account) {
        await bankAccountService.updateBankAccount(account._id, accountData);
        toast.success("Rekening berhasil diperbarui");
      } else {
        await bankAccountService.createBankAccount(accountData);
        toast.success("Rekening berhasil dibuat");
      }

      onClose(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal menyimpan rekening";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Rekening" : "Tambah Rekening"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Ubah informasi rekening bank"
              : "Tambahkan rekening bank baru"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Type Selection - First */}
            <div className="space-y-2">
              <Label htmlFor="tipe">Tipe *</Label>
              <Select value={tipe} onValueChange={(value) => {
                setTipe(value as "ORDA" | "ORLOK" | "");
                // Clear city when switching to ORDA
                if (value === "ORDA") {
                  setCity(undefined);
                }
              }}>
                <SelectTrigger id="tipe">
                  <SelectValue placeholder="Pilih tipe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ORDA">ORDA</SelectItem>
                  <SelectItem value="ORLOK">ORLOK</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Province and City Selection - Conditional based on tipe - Second */}
            {tipe && (
              <div className="space-y-2">
                {tipe === "ORDA" ? (
                  // ORDA: Only show province
                  <>
                    <Label htmlFor="province-only">Provinsi *</Label>
                    <Select
                      value={province}
                      onValueChange={(value) => {
                        setProvince(value === "no-data" || value === "" ? undefined : value);
                        setCity(undefined); // Clear city for ORDA
                      }}
                      disabled={loadingProvinces}
                    >
                      <SelectTrigger id="province-only" className="w-full">
                        {loadingProvinces ? (
                          <div className="flex items-center">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            <span>Memuat provinsi...</span>
                          </div>
                        ) : (
                          <SelectValue placeholder="Pilih Provinsi" />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {provinces.length === 0 && !loadingProvinces ? (
                          <SelectItem value="no-data" disabled>
                            Tidak ada provinsi
                          </SelectItem>
                        ) : (
                          provinces.map((p) => (
                            <SelectItem key={p} value={p}>
                              {p}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  // ORLOK: Show both province and city
                  <>
                    <Label>Provinsi & Kota/Kabupaten *</Label>
                    <LocationSelect
                      province={province}
                      city={city}
                      onProvinceChange={(value) => {
                        setProvince(value);
                        setCity(undefined); // Clear city when province changes
                      }}
                      onCityChange={setCity}
                      showLabels={false}
                    />
                  </>
                )}
              </div>
            )}

            {/* Bank Selection with Search - Third */}
            <div className="space-y-2">
              <Label htmlFor="bank">Bank *</Label>
              <Popover open={bankSearchOpen} onOpenChange={setBankSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={bankSearchOpen}
                    className="w-full justify-between"
                    id="bank"
                  >
                    {selectedBank
                      ? `${selectedBank.name} (${selectedBank.code})`
                      : "Pilih bank"}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <div className="flex items-center border-b px-3">
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <Input
                      placeholder="Cari bank..."
                      value={bankSearchQuery}
                      onChange={(e) => setBankSearchQuery(e.target.value)}
                      className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                  <div className="max-h-[300px] overflow-auto p-1">
                    {filteredBanks.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        Tidak ada bank ditemukan
                      </div>
                    ) : (
                      filteredBanks.map((bank) => (
                        <button
                          key={bank._id}
                          type="button"
                          className={cn(
                            "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                            bankId === bank._id && "bg-accent text-accent-foreground"
                          )}
                          onClick={() => {
                            setBankId(bank._id);
                            setBankSearchOpen(false);
                            setBankSearchQuery("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              bankId === bank._id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex-1 text-left">
                            <div className="font-medium">{bank.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {bank.code}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Account Number */}
            <div className="space-y-2">
              <Label htmlFor="accountNumber">Nomor Rekening *</Label>
              <div className="flex gap-2">
                <Input
                  id="accountNumber"
                  value={accountNumber}
                  onChange={(e) => {
                    setAccountNumber(e.target.value);
                    setValidationResponse(null); // Clear validation when account number changes
                  }}
                  placeholder="Masukkan nomor rekening"
                  required
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleValidate}
                  disabled={validating || !accountNumber.trim() || !bankId}
                >
                  {validating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Validasi"
                  )}
                </Button>
              </div>
            </div>

            {/* Account Name */}
            <div className="space-y-2">
              <Label htmlFor="accountName">Nama Pemilik Rekening *</Label>
              <Input
                id="accountName"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="Masukkan nama rekening atau klik Validasi untuk auto-fill"
                required
                readOnly
                className="bg-muted cursor-not-allowed"
              />
            </div>

            {/* Is Active */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(checked === true)}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Aktif
              </Label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Batal
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? "Simpan" : "Buat"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

